<template>
  <UIInputSelect v-model="criteriaComparison" :size :data :disabled />
  <template v-if="isFieldVisible">
    <UIInputAutocomplete
      v-if="mode === 'autocomplete'"
      v-model="criteriaValue"
      :data="connectorsList"
      :size
      :disabled
      @ready="el => el.focus()"
    />
    <UIInputText v-if="mode === 'input'" v-model="criteriaValue" :size :disabled />
  </template>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, watch } from 'vue'

import {
  FilterComparison,
  DropdownListData,
  TransactionRuleCriteria,
  InputSize,
} from '@types'

import useFiltersService from '@/services/filters'
import { getComparisonData } from '@/components/UI/Filter/components/utils/helpers'

import { UIInputAutocomplete, UIInputSelect, UIInputText } from '@ui/fields'

type Props = {
  disabled?: boolean
}

defineProps<Props>()

const modelValue = defineModel<TransactionRuleCriteria['params'][number]>({
  required: true,
})

const { fetchOptions } = useFiltersService()

const size = inject<InputSize>('size')

const connectorsList = ref<DropdownListData[]>([])

const criteriaComparison = computed({
  get() {
    return modelValue.value.comparison
  },
  set(comparison) {
    modelValue.value = {
      ...modelValue.value,
      comparison,
    }
  },
})

const criteriaValue = computed({
  get() {
    return modelValue.value.value
  },
  set(value) {
    modelValue.value = {
      ...modelValue.value,
      value,
    }
  },
})

const data = computed(() =>
  getComparisonData().map(value => ({
    value,
    label: value,
  })),
)

const mode = computed(() => {
  if (
    criteriaComparison.value &&
    [FilterComparison.EQ, FilterComparison.NOTEQ].includes(
      criteriaComparison.value,
    )
  ) {
    return 'autocomplete'
  } else {
    return 'input'
  }
})

const isFieldVisible = computed(
  () =>
    criteriaComparison.value &&
    ![FilterComparison.EMPTY, FilterComparison.NOTEMPTY].includes(
      criteriaComparison.value,
    ),
)

watch(isFieldVisible, value => {
  criteriaValue.value = value ? '' : undefined
})

onBeforeMount(async () => {
  if (!criteriaComparison.value) {
    criteriaComparison.value = FilterComparison.EQ
  }

  connectorsList.value = await fetchOptions(
    'LinkedDataTransactions',
    'custodian',
  )
})
</script>
